/**
 * ActionAcceptModal - 采纳动作确认弹窗
 * 
 * 功能：
 * 1. 确认采纳动作
 * 2. 可选设置计划执行日期（加入待办事项）
 * 3. 成功后返回结果供父组件刷新
 */
import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X, Check, Calendar, Clock, AlertCircle, Loader2 } from 'lucide-react'
import { clsx } from 'clsx'
import DatePicker from './DatePicker'

interface ActionAcceptModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: (scheduledDate?: string) => Promise<void>
  actionContent: string
  actionGrade: 'A' | 'B' | 'C'
  timeCostDisplay?: string
}

// 等级配置
const GRADE_CONFIG = {
  A: { label: '最佳路径', color: 'text-green-600', bgColor: 'bg-green-100' },
  B: { label: '快速验证', color: 'text-blue-600', bgColor: 'bg-blue-100' },
  C: { label: '止血动作', color: 'text-amber-600', bgColor: 'bg-amber-100' },
}

export default function ActionAcceptModal({
  isOpen,
  onClose,
  onConfirm,
  actionContent,
  actionGrade,
  timeCostDisplay,
}: ActionAcceptModalProps) {
  const [scheduledDate, setScheduledDate] = useState<string | undefined>(undefined)
  const [showDatePicker, setShowDatePicker] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const gradeConfig = GRADE_CONFIG[actionGrade] || GRADE_CONFIG.B

  const handleConfirm = async () => {
    setIsLoading(true)
    setError(null)
    try {
      await onConfirm(scheduledDate)
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : '操作失败，请重试')
    } finally {
      setIsLoading(false)
    }
  }

  const handleClose = () => {
    if (!isLoading) {
      setScheduledDate(undefined)
      setShowDatePicker(false)
      setError(null)
      onClose()
    }
  }

  // 快捷日期选项
  const quickDates = [
    { label: '今天', days: 0 },
    { label: '明天', days: 1 },
    { label: '3天后', days: 3 },
    { label: '下周', days: 7 },
  ]

  const setQuickDate = (days: number) => {
    const date = new Date()
    date.setDate(date.getDate() + days)
    setScheduledDate(date.toISOString().split('T')[0])
    setShowDatePicker(false)
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* 背景遮罩 */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleClose}
            className="fixed inset-0 bg-black/50 z-50"
          />

          {/* 弹窗内容 */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
          >
            <div className="bg-white rounded-2xl shadow-xl max-w-md w-full overflow-hidden">
              {/* 头部 */}
              <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Check className="w-5 h-5 text-green-600" />
                  <h3 className="font-semibold text-gray-900">采纳动作</h3>
                </div>
                <button
                  onClick={handleClose}
                  disabled={isLoading}
                  className="p-1 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* 内容 */}
              <div className="p-6 space-y-4">
                {/* 动作内容 */}
                <div className="p-4 bg-gray-50 rounded-xl">
                  <div className="flex items-center gap-2 mb-2">
                    <span className={clsx(
                      'text-xs font-bold px-2 py-0.5 rounded-full',
                      gradeConfig.bgColor, gradeConfig.color
                    )}>
                      {actionGrade}级 · {gradeConfig.label}
                    </span>
                    {timeCostDisplay && (
                      <span className="text-xs text-gray-500 flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {timeCostDisplay}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-700 line-clamp-3">
                    {actionContent}
                  </p>
                </div>

                {/* 设置执行日期 */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-gray-400" />
                      设置计划执行日期
                    </label>
                    <span className="text-xs text-gray-400">可选</span>
                  </div>

                  {/* 快捷日期 */}
                  <div className="flex flex-wrap gap-2">
                    {quickDates.map((item) => (
                      <button
                        key={item.label}
                        onClick={() => setQuickDate(item.days)}
                        className={clsx(
                          'px-3 py-1.5 text-sm rounded-lg transition-colors',
                          scheduledDate && 
                          scheduledDate === new Date(Date.now() + item.days * 86400000).toISOString().split('T')[0]
                            ? 'bg-primary-100 text-primary-700'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        )}
                      >
                        {item.label}
                      </button>
                    ))}
                    <button
                      onClick={() => setShowDatePicker(!showDatePicker)}
                      className={clsx(
                        'px-3 py-1.5 text-sm rounded-lg transition-colors',
                        showDatePicker
                          ? 'bg-primary-100 text-primary-700'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      )}
                    >
                      选择日期...
                    </button>
                  </div>

                  {/* 日期选择器 */}
                  {showDatePicker && (
                    <div className="mt-2">
                      <DatePicker
                        value={scheduledDate}
                        onChange={(date) => {
                          setScheduledDate(date)
                          setShowDatePicker(false)
                        }}
                      />
                    </div>
                  )}

                  {/* 已选日期显示 */}
                  {scheduledDate && (
                    <div className="flex items-center justify-between px-3 py-2 bg-primary-50 border border-primary-100 rounded-lg">
                      <span className="text-sm text-primary-700">
                        计划执行日期：{new Date(scheduledDate).toLocaleDateString('zh-CN', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          weekday: 'long'
                        })}
                      </span>
                      <button
                        onClick={() => setScheduledDate(undefined)}
                        className="text-primary-600 hover:text-primary-800"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>

                {/* 提示 */}
                <div className="flex items-start gap-2 text-xs text-gray-500">
                  <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span>
                    采纳后，此动作将加入待办事项列表。设置执行日期可帮助您更好地管理任务。
                  </span>
                </div>

                {/* 错误提示 */}
                {error && (
                  <div className="px-3 py-2 bg-red-50 border border-red-100 rounded-lg text-sm text-red-600">
                    {error}
                  </div>
                )}
              </div>

              {/* 底部按钮 */}
              <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-end gap-3">
                <button
                  onClick={handleClose}
                  disabled={isLoading}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  取消
                </button>
                <button
                  onClick={handleConfirm}
                  disabled={isLoading}
                  className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      处理中...
                    </>
                  ) : (
                    <>
                      <Check className="w-4 h-4" />
                      确认采纳
                    </>
                  )}
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}
